import React, {useEffect, useState} from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Badge,
  Button,
  Container,
} from "reactstrap";
import {ChartSkeleton} from "./SkeletonLoader";
import {formatDate} from "../utils/dates";
import ModernStockChart from "./ModernStockChart";
import ModernStockTable from "./ModernStockTable";
import ForwardDistribution from "./ForwardDistribution";
import ForwardCurveFanChart from "./ForwardCurveFanChart";
import MatchCandlestickChart from "./MatchCandlestickChart";
import EarningsBadge from "./EarningsBadge";
import FundamentalsChip from "./FundamentalsChip";
import {displaySymbol, displayName} from "../utils/delisted";
import {matchHistoricalEndIndex, matchHistoricalPointCount} from "../utils/matchWindow";
import {WINDOW_LABELS_LONG} from "../utils/windows";

// Define interface for table data
interface TableDataRow {
  symbol: string;
  symbol_name?: string;
  score: number;
  start_date: string;
  pricedLastDay: string;
  pricedResolution: string;
  priceChange: number;
  window: number;
  origin_window: number;
  forecast_window: number;
}

interface ChartProps {
  query: StockChartQuery;
  matches: MatchesState[];
  loading?: boolean;
  forwardDistribution?: ForwardDistribution | null;
  forwardCurve?: ForwardCurve | null;
}

const StockChartDisplay = ({query, matches, loading, forwardDistribution, forwardCurve}: ChartProps) => {
  const [tableData, setTableData] = useState<TableDataRow[]>([]);
  const [hoveredMatchIndex, setHoveredMatchIndex] = useState<number | null>(null);
  // Global line/candlestick toggle for all match cards — one switch (rather than
  // a per-card control) keeps the analog comparison consistent and avoids
  // cluttering each of the 6-12 cards with its own switch. Candlestick mode
  // swaps in MatchCandlestickChart (Chart.js, styled like PatternDetail's main
  // chart) — a 1-for-1 swap with ModernStockChart (never shown alongside it),
  // so it doesn't add to the per-card chart instance count.
  const [matchChartMode, setMatchChartMode] = useState<"line" | "candle">("line");
  // forwardCurve.curve always has one entry per horizon in the grid (structural —
  // built by mapping over `horizons`), even when there aren't enough historical
  // analogs with enough subsequent price history to compute returns that far out
  // (common for larger windows/horizons, where curve_h_max grows but match_ohlcv
  // can't reach that far for matches near the end of the data range). In that
  // case every entry has count: 0 / null stats, so `curve.length > 0` alone isn't
  // a reliable "is there anything to plot" signal — check for actual data instead,
  // and fall back to the legacy single-horizon ForwardDistribution (which already
  // self-hides on count === 0) so the user at least sees the headline-horizon stats
  // if those are available.
  const curveWithData = forwardCurve && forwardCurve.curve.some((entry) => entry.count > 0) ? forwardCurve : null;
  // Prepare data for the table
  useEffect(() => {
    if (matches.length > 0) {
      const resolutionDay = query.forecast_window === 0 ? 5 : query.forecast_window;

      const tableRows: TableDataRow[] = matches.map((match) => {
        const lastDayIndex = matchHistoricalEndIndex(match, resolutionDay);
        const pLastDay = match.yaxis[lastDayIndex];
        const pResolution = match.yaxis[lastDayIndex + resolutionDay];
        const priceChange = ((pResolution - pLastDay) / pLastDay) * 100;

        return {
          symbol: displaySymbol(match.symbol, match.delisted),
          symbol_name: displayName(match.symbol_name),
          score: match.score! * 100,
          start_date: match.xaxis[0].split("T")[0],
          pricedLastDay: "$" + pLastDay.toFixed(2),
          pricedResolution: "$" + pResolution.toFixed(2),
          priceChange: priceChange,
          // Link a hybrid analog back to the horizon it actually came from, not
          // the source query's horizon.
          window: match.origin_window,
          origin_window: match.origin_window,
          forecast_window: resolutionDay,
        };
      });

      setTableData(tableRows);
    }
  }, [matches, query.forecast_window]);

  if (loading) {
    return (
      <Container className="query-results-container">
        <Card className="main-chart-card">
          <CardBody>
            <ChartSkeleton />
          </CardBody>
        </Card>
      </Container>
    );
  }

  if (!query.symbol || matches.length === 0) {
    return null;
  }

  // Calculate forecast data for the query chart
  const calculateForecastData = () => {
    const windowIndex = query.window - 1;
    const resolutionDay = query.forecast_window === 0 ? 5 : query.forecast_window;

    // Calculate the percentage of matches that went up vs down
    const upMatches = matches.filter((match) => {
      const matchWindowIndex = matchHistoricalEndIndex(match, resolutionDay);
      return match.yaxis[matchWindowIndex + resolutionDay] > match.yaxis[matchWindowIndex];
    });

    const downMatches = matches.filter((match) => {
      const matchWindowIndex = matchHistoricalEndIndex(match, resolutionDay);
      return match.yaxis[matchWindowIndex + resolutionDay] < match.yaxis[matchWindowIndex];
    });

    const upPercent = (upMatches.length / matches.length) * 100;
    const downPercent = (downMatches.length / matches.length) * 100;

    // Calculate average up and down movements
    const upMoves = upMatches.map(
      (match) => {
        const matchWindowIndex = matchHistoricalEndIndex(match, resolutionDay);
        return ((match.yaxis[matchWindowIndex + resolutionDay] - match.yaxis[matchWindowIndex]) /
          match.yaxis[matchWindowIndex]) * 100;
      },
    );

    const downMoves = downMatches.map(
      (match) => {
        const matchWindowIndex = matchHistoricalEndIndex(match, resolutionDay);
        return ((match.yaxis[matchWindowIndex + resolutionDay] - match.yaxis[matchWindowIndex]) /
          match.yaxis[matchWindowIndex]) * 100;
      },
    );

    const allMoves = [...upMoves, ...downMoves];

    // Average move = ARITHMETIC mean of the per-match returns — one definition,
    // matching the backend's avg_up/avg_dn (which feed the track record / receipts)
    // and the server's forward_distribution. (Previously these chart projection
    // lines used a geometric-mean-of-magnitudes/100 for up/down and a compounding
    // geometric return for "all", so the projected averages disagreed with the
    // "Forward Outcome" panel computed over the same match set.) Returns are in %,
    // converted to a fraction here for the `lastPrice * (1 ± move)` projections.
    const mean = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
    const avgUpMove = upMoves.length > 0 ? mean(upMoves) / 100 : 0;
    const avgDownMove = downMoves.length > 0 ? mean(downMoves.map(Math.abs)) / 100 : 0;
    const avgAllMove = allMoves.length > 0 ? mean(allMoves) / 100 : 0;

    // Calculate max moves
    const maxUpMove = upMoves.length > 0 ? Math.max(...upMoves) / 100 : 0;
    const maxDownMove = downMoves.length > 0 ? Math.min(...downMoves) / 100 : 0;

    // Create forecast data objects
    const lastPrice = query.yaxis[windowIndex];

    // Up avg forecast
    const dataUpAvg = {
      ...query,
      label: "Avg Up",
      yaxis: [...query.yaxis],
    };

    // Up max forecast
    const dataUpMax = {
      ...query,
      label: "Max Up",
      yaxis: [...query.yaxis],
    };

    // Down avg forecast
    const dataDownAvg = {
      ...query,
      label: "Avg Down",
      yaxis: [...query.yaxis],
    };

    // Down max forecast
    const dataDownMax = {
      ...query,
      label: "Max Down",
      yaxis: [...query.yaxis],
    };

    // Overall avg forecast
    const dataAllAvg = {
      ...query,
      label: "Overall Avg",
      yaxis: [...query.yaxis],
    };

    // Calculate forecast points
    for (let i = 1; i <= resolutionDay; i++) {
      const factor = i / resolutionDay;

      // Set forecast day labels
      dataUpAvg.xaxis[windowIndex + i] = "Day " + i;
      dataUpMax.xaxis[windowIndex + i] = "Day " + i;
      dataDownAvg.xaxis[windowIndex + i] = "Day " + i;
      dataDownMax.xaxis[windowIndex + i] = "Day " + i;
      dataAllAvg.xaxis[windowIndex + i] = "Day " + i;

      // Zero out pre-forecast points to make the forecast lines start at the right point
      if (i === 1) {
        for (let j = 0; j < windowIndex; j++) {
          dataUpAvg.yaxis[j] = NaN;
          dataUpMax.yaxis[j] = NaN;
          dataDownAvg.yaxis[j] = NaN;
          dataDownMax.yaxis[j] = NaN;
          dataAllAvg.yaxis[j] = NaN;
        }
      }

      // Set forecast points
      dataUpAvg.yaxis[windowIndex + i] = lastPrice * (1 + avgUpMove * factor);
      dataUpMax.yaxis[windowIndex + i] = lastPrice * (1 + maxUpMove * factor);
      dataDownAvg.yaxis[windowIndex + i] = lastPrice * (1 - avgDownMove * factor);
      dataDownMax.yaxis[windowIndex + i] = lastPrice * (1 + maxDownMove * factor);
      dataAllAvg.yaxis[windowIndex + i] = lastPrice * (1 + avgAllMove * factor);
    }

    // Median-analog projection cone: per-horizon median + 10th/90th pct of the
    // matches' forward returns (same idea as the Top Matches page). Two invisible
    // band edges (p10/p90) + a dashed median line rendered by ModernStockChart.
    const quantile = (sorted: number[], q: number): number =>
      sorted.length ? sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor(sorted.length * q)))] : NaN;
    const dataMedian = {...query, label: "Projected (median)", yaxis: [...query.yaxis]};
    const dataP10 = {...query, label: "", yaxis: [...query.yaxis]};
    const dataP90 = {...query, label: "", yaxis: [...query.yaxis]};
    for (let i = 1; i <= resolutionDay; i++) {
      const rets: number[] = [];
      for (const m of matches) {
        const matchWindowIndex = matchHistoricalEndIndex(m, resolutionDay);
        const base = m.yaxis[matchWindowIndex];
        const fwd = m.yaxis[matchWindowIndex + i];
        if (Number.isFinite(base) && Number.isFinite(fwd) && base > 0) rets.push(fwd / base - 1);
      }
      rets.sort((a, b) => a - b);
      const enough = rets.length >= 3;
      const med = enough ? quantile(rets, 0.5) : NaN;
      const lo = enough ? quantile(rets, 0.1) : NaN;
      const hi = enough ? quantile(rets, 0.9) : NaN;
      dataMedian.xaxis[windowIndex + i] = "Day " + i;
      dataP10.xaxis[windowIndex + i] = "Day " + i;
      dataP90.xaxis[windowIndex + i] = "Day " + i;
      if (i === 1) {
        for (let j = 0; j < windowIndex; j++) {
          dataMedian.yaxis[j] = NaN;
          dataP10.yaxis[j] = NaN;
          dataP90.yaxis[j] = NaN;
        }
      }
      dataMedian.yaxis[windowIndex + i] = Number.isFinite(med) ? lastPrice * (1 + med) : NaN;
      dataP10.yaxis[windowIndex + i] = Number.isFinite(lo) ? lastPrice * (1 + lo) : NaN;
      dataP90.yaxis[windowIndex + i] = Number.isFinite(hi) ? lastPrice * (1 + hi) : NaN;
    }

    return {
      upPercent,
      downPercent,
      avgUpMove: avgUpMove * 100,
      avgDownMove: avgDownMove * 100,
      maxUpMove: maxUpMove * 100,
      maxDownMove: maxDownMove * 100,
      avgAllMove: avgAllMove * 100,
      dataUpAvg,
      dataUpMax,
      dataDownAvg,
      dataDownMax,
      dataAllAvg,
      dataMedian,
      dataP10,
      dataP90,
    };
  };

  const forecast = calculateForecastData();

  const chartProps = {
    data: query,
    dataUpMax: forecast.dataUpMax,
    dataUpAvg: forecast.dataUpAvg,
    dataDownMax: forecast.dataDownMax,
    dataDownAvg: forecast.dataDownAvg,
    dataAllAvg: forecast.dataAllAvg,
    dataMedian: forecast.dataMedian,
    dataP10: forecast.dataP10,
    dataP90: forecast.dataP90,
    // Default on: Projected (median), Overall Avg, Avg Up, Avg Down. Max Up/Down
    // start off; users check them on via the checkbox legend.
    defaultHidden: ["Max Up", "Max Down"],
  };

  // Format period date range
  const formatPeriodRange = () => {
    try {
      return `Period: ${formatDate(query.xaxis[0], 0)} - ${formatDate(query.xaxis[query.window - 1], 0)}`;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      // Fallback if date formatting fails
      return query.label || "";
    }
  };

  return (
    <Container className="query-results-container">
      {/* Analysis chart with forecast lines */}
      <div className="analysis-section">
        <h3 className="chart-section-title mb-1">{query.symbol_name} ({query.symbol}) Pattern Analysis</h3>
        <div className="mb-2">
          <FundamentalsChip ticker={query.symbol} />
        </div>
        <Card className="main-chart-card">
          <CardBody>
            <div className="chart-options">
              <div className="chart-period">
                <Badge color="light" className="date-badge">
                  {formatPeriodRange()}
                </Badge>
                <Badge color="light" className="window-badge">
                  {query.window * query.step}-Day Window
                </Badge>
                {query.beta !== undefined && (
                  <Badge
                    color="light"
                    className="beta-badge"
                    title={`Trailing market beta vs SPY as of the query date${
                      query.symbol ? ` (${query.symbol})` : ""
                    }`}
                  >
                    β {query.beta.toFixed(2)}
                  </Badge>
                )}
              </div>
              <div className="chart-container">
                <ModernStockChart {...chartProps} />
              </div>
            </div>

            {/* Stats table for the forecasts */}
            <div className="forecast-stats">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th colSpan={2} scope="colgroup" className="stats-header">
                    Percent Move
                    </th>
                    <th colSpan={3} scope="colgroup" className="stats-header">
                    Average Move
                    </th>
                    <th colSpan={2} scope="colgroup" className="stats-header">
                    Max Move
                    </th>
                  </tr>
                  <tr>
                    <th scope="col" className="stats-label">Up</th>
                    <th scope="col" className="stats-label">Down</th>
                    <th scope="col" className="stats-label">Up</th>
                    <th scope="col" className="stats-label">Down</th>
                    <th scope="col" className="stats-label">All</th>
                    <th scope="col" className="stats-label">Up</th>
                    <th scope="col" className="stats-label">Down</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="stats-value up">{forecast.upPercent.toFixed(1)}%</td>
                    <td className="stats-value down">{forecast.downPercent.toFixed(1)}%</td>
                    <td className="stats-value up">{forecast.avgUpMove.toFixed(1)}%</td>
                    <td className="stats-value down">{forecast.avgDownMove.toFixed(1)}%</td>
                    <td className={
                      "stats-value " +
                      (forecast.avgAllMove > 0
                        ? "up"
                        : forecast.avgAllMove < 0
                          ? "down"
                          : "")
                    }
                    >
                      {forecast.avgAllMove.toFixed(1)}%</td>
                    <td className="stats-value up">{forecast.maxUpMove.toFixed(1)}%</td>
                    <td className="stats-value down">{forecast.maxDownMove.toFixed(1)}%</td>
                  </tr>
                </tbody>
              </table>
              <div className="text-muted" style={{fontSize: 11, marginTop: 6}}>
                Tip: use the <strong>checkboxes above the chart</strong> to show/hide projection lines.
                The shaded band is the middle 80% (10th–90th pct) of analog outcomes; the dashed line is
                the median path.
                {matches.some((m) => m.delisted) && (
                  <> &nbsp;·&nbsp; <strong>*</strong> delisted security — no longer trades; shown as a historical analog.</>
                )}
              </div>
            </div>

            {curveWithData ? (
              <ForwardCurveFanChart curve={curveWithData} />
            ) : (
              forwardDistribution && forwardDistribution.count > 0 && (
                <ForwardDistribution
                  distribution={forwardDistribution}
                  forecastWindow={query.forecast_window}
                  window={query.window}
                />
              )
            )}
          </CardBody>
        </Card>
      </div>

      {/* Matching charts section */}
      <div className="matching-charts-section">
        <div className="matching-charts-header">
          <h3 className="chart-section-title mb-0">Top {matches.length} matching charts</h3>
          <div className="match-chart-mode-toggle" role="group" aria-label="Match chart display mode">
            <Button
              size="sm"
              color={matchChartMode === "line" ? "primary" : "outline-secondary"}
              onClick={() => setMatchChartMode("line")}
            >
              Line
            </Button>
            <Button
              size="sm"
              color={matchChartMode === "candle" ? "primary" : "outline-secondary"}
              onClick={() => setMatchChartMode("candle")}
            >
              Candlestick
            </Button>
          </div>
        </div>
        <Row className="chart-grid">
          {matches.map((match, index) => {
            // Calculate percent change for this match
            const resolutionDay = query.forecast_window === 0 ? 5 : query.forecast_window;
            const historicalPointCount = matchHistoricalPointCount(match, resolutionDay);
            const windowIndex = Math.max(0, historicalPointCount - 1);
            const startPrice = match.yaxis[windowIndex];
            const endPrice = match.yaxis[windowIndex + resolutionDay];
            const pctChange = ((endPrice - startPrice) / startPrice) * 100;
            const isPositive = pctChange >= 0;

            return (
              <Col
                sm="6"
                lg="4"
                key={`${match.symbol}-${index}`}
                className="chart-grid-item"
              >
                <Card className="match-chart-card">
                  <CardHeader className="match-chart-header">
                    <div className="match-chart-title">
                      <span
                        className="match-symbol"
                        onMouseEnter={() => setHoveredMatchIndex(index)}
                        onMouseLeave={() => setHoveredMatchIndex(null)}
                      >
                        {hoveredMatchIndex === index ? displayName(match.symbol_name) : displaySymbol(match.symbol, match.delisted)}
                      </span>
                      <span className="match-date"><span className="match-date-label">Start:</span> {formatDate(match.label, 1)}</span>
                    </div>
                    <EarningsBadge ticker={match.symbol} className="mb-1" />
                    <Badge
                      color="light"
                      className="origin-window-badge"
                      title="Historical horizon this analog was retrieved from"
                    >
                      {WINDOW_LABELS_LONG[match.origin_window] ?? `${match.origin_window} days`} origin
                    </Badge>
                    <div className="percent-change">
                      <span className={isPositive ? "up-indicator" : "down-indicator"}>
                        {isPositive ? "▲" : "▼"} {Math.abs(pctChange).toFixed(2)}%
                      </span>
                    </div>
                    <div className="match-score">
                      <div className="score-bar">
                        <div
                          className="score-fill"
                          style={{
                            width: `${(match.score! * 100).toFixed(0)}%`,
                          }}
                        ></div>
                      </div>
                      <span className="score-value">
                        {(match.score! * 100).toFixed(0)}%
                      </span>
                    </div>
                    {match.beta !== undefined && (
                      <div
                        className="beta-chip"
                        title={`Trailing market beta ${match.beta.toFixed(2)}${
                          match.beta_delta !== undefined
                            ? ` (${match.beta_delta >= 0 ? "+" : ""}${match.beta_delta.toFixed(2)} vs ${query.symbol})`
                            : ""
                        }`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          marginTop: 4,
                          padding: "1px 7px",
                          borderRadius: 10,
                          fontSize: "0.72rem",
                          fontVariantNumeric: "tabular-nums",
                          border: "1px solid var(--bs-border-color, #d0d0d0)",
                          color: "var(--bs-secondary-color, #666)",
                        }}
                      >
                        <span style={{fontWeight: 600}}>β&nbsp;{match.beta.toFixed(2)}</span>
                        {match.beta_delta !== undefined && (
                          <span
                            style={{
                              color:
                                Math.abs(match.beta_delta) <= 0.5
                                  ? "var(--bs-success, #2e7d32)"
                                  : "var(--bs-warning-text, #b26a00)",
                            }}
                          >
                            {match.beta_delta >= 0 ? "+" : ""}
                            {match.beta_delta.toFixed(2)}
                          </span>
                        )}
                      </div>
                    )}
                  </CardHeader>
                  <CardBody className="match-chart-body">
                    {matchChartMode === "candle" && match.ohlcv.length > 0 ? (
                      <MatchCandlestickChart
                        ohlcv={match.ohlcv}
                        symbol={displaySymbol(match.symbol, match.delisted)}
                        historicalEndIndex={windowIndex}
                      />
                    ) : (
                      <ModernStockChart
                        data={{...match, symbol: displaySymbol(match.symbol, match.delisted), window: historicalPointCount}}
                        historicalPointCount={historicalPointCount}
                        isCompact={true}
                      />
                    )}
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>

      {/* Breakdown table section */}
      <div className="breakdown-section">
        <h3 className="chart-section-title">Breakdown</h3>
        <ModernStockTable data={tableData} />
      </div>

      <div className="disclaimer-footer">
        <p>
          Past performance does not guarantee future results. Historical pattern
          matching is provided for informational purposes only.
        </p>
      </div>
    </Container>
  );
};

export default StockChartDisplay;
